import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Spinner } from "../ui/spinner";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import axios from "axios";
import moment from "moment";
import { Eye, Edit, Search, Trash2 } from "lucide-react";
import routes from "../../routes/routes";
import { getConfig } from "../../utils/permissions";
import { convertObject } from "../../utils/common";
import { StatusArr, getStatusDisplayLabel } from "../../utils/status-utils";
import StatusEditor from "../common-components/status-editor/status-editor";
import * as XLSX from "xlsx";

export default function AllReleases({ setPageTitle }) {
  const [tableLoading, setTableLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [selectTag, setselectTag] = useState(null);

  const [totalCount, setTotalCount] = useState(0);
  const [releaseList, setReleaseList] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [state, setState] = useState({
    searchText: "",
    searchedColumn: "",
  });
  const [showStatusDropDown, setShowStatusDropDown] = useState(true);
  const searchInput = useRef();

  // Initialize pagination state from session storage
  const [currentPage, setCurrentPage] = useState(() => {
    const stored = sessionStorage.getItem("currentPage");
    return stored ? JSON.parse(stored).current : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const stored = sessionStorage.getItem("currentPage");
    return stored ? JSON.parse(stored).pageSize : 12;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  useEffect(() => setPageTitle("All Releases"), [totalCount, setPageTitle]);

  const onSelectChange = (newSelectedRowKeys) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const hasSelected = selectedRowKeys.length > 0;

  const downloadAlbums = () => {
    if (selectTag == null) {
      return;
    }

    axios
      .post("/admin/configure/export", {
        providerId: selectTag,
        albums: selectedRowKeys,
      })
      .then((response) => {
        const result = response.data.data;

        // Get the current date in 'YYYY-MM-DD' format
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split("T")[0];

        const selectedTagName =
          tags.find((t) => t.id === selectTag)?.name || "Unknown";
        const fileName = `${selectedTagName}_${formattedDate}.xlsx`;

        const wb = XLSX.utils.book_new();

        // Function to convert JSON data to AOA (Array of Arrays)
        const jsonToAoa = (jsonArray) => {
          const keys = Object.keys(jsonArray[0]);
          const aoa = [keys];

          jsonArray.forEach((obj) => {
            const row = keys.map((key) => obj[key]);
            aoa.push(row);
          });

          return aoa;
        };

        // Loop through the result to create sheets dynamically
        result.forEach((sheetData) => {
          const sheetName = Object.keys(sheetData)[0];
          const sheetContent = sheetData[sheetName];
          const aoa = jsonToAoa(sheetContent);
          const ws = XLSX.utils.aoa_to_sheet(aoa);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });

        XLSX.writeFile(wb, fileName);
      })
      .catch((error) => {
        console.error("Error occurred while exporting data:", error);
      });
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);

    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    // Set new timer for debounced search
    const newTimer = setTimeout(() => {
      // Reset to first page when searching
      setCurrentPage(1);

      // Prepare search filters
      const searchFilters = {};
      if (searchValue.trim()) {
        searchFilters.search = {
          value: searchValue.trim(),
          fields: ["title", "primaryArtist", "fingerprint"],
        };
      }

      // Call API with search filters
      handleChange({ current: 1, pageSize }, searchFilters, {});
    }, 500); // 500ms debounce

    setSearchDebounceTimer(newTimer);
  };

  const getAllReleases = (limit, offset, filters) => {
    setTableLoading(true);
    let searchStrings = JSON.stringify(convertObject(filters));
    axios
      .get("/admin/albums", {
        params: { limit: limit, offset: offset, searchFields: searchStrings },
      })
      .then((response) => {
        let albums = response.data.data.albums;

        if (albums.length) {
          albums = albums.map((a) => {
            if (!a || !a.belongsTo || !a.user?.length) {
              return a;
            }
            a.key = a._id;
            a.uploadedBy =
              a.user[0].name || a.user[0].firstName + " " + a.user[0].lastName;
            a.creatorEmail = a.user[0].email;
            a.creatorMobile = a.user[0].phoneCountryCode
              ? a.user[0].phoneCountryCode + " " + a.user[0].phone
              : a.user[0].phone;
            return a;
          });
          setReleaseList(albums);
          setTotalCount(response.data.data.totalCount);
        } else {
          setReleaseList([]);
        }
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const getAllProvider = () => {
    axios.get("/admin/configure/get-tags").then((response) => {
      setTags(response.data.data);
    });
  };

  const updateSelectedTag = (event) => {
    setselectTag(event);
    // console.log(selectTag)
  };

  useEffect(() => {
    let getPagination = sessionStorage.getItem("currentPage");
    if (getPagination) {
      let pagination = JSON.parse(getPagination);
      handleChange(pagination, null, {});
    } else {
      getAllReleases(10, 0, {});
    }
    getAllProvider();
    // eslint-disable-next-line
  }, []);

  // Cleanup search timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  const getData = (offset, limit, params, filters) => {
    getAllReleases(limit, offset, filters);
  };

  const handleChange = (pagination, filters, sorter) => {
    sessionStorage.setItem("currentPage", JSON.stringify(pagination));
    const offset =
      pagination.current * pagination.pageSize - pagination.pageSize;
    const limit = pagination.pageSize;
    const params = {};

    if (sorter.hasOwnProperty("column")) {
      params.order = { field: sorter.field, dir: sorter.order };
    }

    getData(offset, limit, params, filters);
  };

  const onBulkStatusChange = (status, selected) => {
    axios
      .patch("/admin/albums/status", {
        status: status,
        albums: selected,
      })
      .then(() => {
        setReleaseList((albums) => {
          const albumsCopy = [...albums];

          albumsCopy.forEach((album, index) => {
            console.log(albums, album._id);
            if (selected.includes(album._id)) {
              albumsCopy[index].status = status;
            }
          });
          return albumsCopy;
        });
        setShowStatusDropDown(false);
        setTimeout(() => setShowStatusDropDown(true), 10);
        setSelectedRowKeys([]);
        toast.success(
          `Updated album status to ${getStatusDisplayLabel(status)}`
        );
      })
      .catch(() => {
        toast.error("Failed changing album status");
      });
  };

  const deleteAlbum = (album) => {
    if (
      window.prompt(
        `Type "${album.title || album.fingerprint}" to delete this album`
      ) !== album.title &&
      album.fingerprint
    )
      return;

    toast.info("Deleting album...");
    axios
      .delete("/admin/albums/" + album._id)
      .then(() => {
        toast.success("Album deleted");
        setReleaseList((rl) => rl.filter((r) => r._id !== album._id));
      })
      .catch(() => {
        toast.error("Error deleting album");
      });
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  return (
    <div className="w-full h-full bg-background overflow-y-auto">
      <div className="space-y-4 py-2">
        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <Input
            placeholder="Search releases..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="max-w-sm ml-2"
          />
          {selectedRowKeys.length > 0 && (
            <div className="flex items-center space-x-2">
              <Select
                onValueChange={(value) =>
                  onBulkStatusChange(value, selectedRowKeys)
                }
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Bulk change status" />
                </SelectTrigger>
                <SelectContent>
                  {StatusArr.map((status) => (
                    <SelectItem key={status} value={status}>
                      <span>{getStatusDisplayLabel(status)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select onValueChange={updateSelectedTag}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={downloadAlbums}>
                Download ({selectedRowKeys.length})
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={
                      selectedRowKeys.length === releaseList.length &&
                      releaseList.length > 0
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedRowKeys(releaseList.map((r) => r._id));
                      } else {
                        setSelectedRowKeys([]);
                      }
                    }}
                    className="ml-4"
                  />
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Release ID
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold text-right">
                  Actions
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Status
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Release Title
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Primary Artist
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Uploaded By
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  UPC
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Release Date
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Created Date
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Creator Email
                </TableHead>
                <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                  Creator Mobile
                </TableHead>
                {getConfig("privilege.can-delete-releases") && (
                  <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                    Delete
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8">
                    <div className="flex justify-center items-center">
                      <Spinner />
                    </div>
                  </TableCell>
                </TableRow>
              ) : releaseList.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No releases found
                  </TableCell>
                </TableRow>
              ) : (
                releaseList.map((album, index) => (
                  <TableRow key={album._id} className="hover:bg-muted/30">
                    <TableCell>
                      <Checkbox
                        checked={selectedRowKeys.includes(album._id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRowKeys([...selectedRowKeys, album._id]);
                          } else {
                            setSelectedRowKeys(
                              selectedRowKeys.filter((id) => id !== album._id)
                            );
                          }
                        }}
                        className="ml-4"
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {album.fingerprint}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button variant="outline" size="icon" asChild>
                          <Link to={routes.viewRelease + "/" + album._id}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="outline" size="icon" asChild>
                          <Link to={routes.editRelease + "/" + album._id}>
                            <Edit className="h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {!tableLoading && showStatusDropDown ? (
                        <StatusEditor
                          album={album}
                          onStatusChange={(updatedAlbum, newStatus) => {
                            setReleaseList((albums) => {
                              const albumsCopy = [...albums];
                              albumsCopy[index] = updatedAlbum;
                              return albumsCopy;
                            });
                          }}
                        />
                      ) : (
                        <div className="h-10 w-48" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={routes.viewRelease + "/" + album._id}
                        className="text-foreground hover:underline"
                      >
                        {album.title || (
                          <span className="italic text-gray-500">
                            No information available
                          </span>
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>{album.primaryArtist}</TableCell>
                    <TableCell>{album.uploadedBy}</TableCell>
                    <TableCell>{album.UPC}</TableCell>
                    <TableCell>
                      {album.releaseDate
                        ? moment(album.releaseDate).format("MMMM Do YYYY")
                        : ""}
                    </TableCell>
                    <TableCell>
                      {album.createdAt
                        ? moment(album.createdAt).format("MMMM Do YYYY")
                        : ""}
                    </TableCell>
                    <TableCell>{album.creatorEmail}</TableCell>
                    <TableCell>{album.creatorMobile}</TableCell>
                    {getConfig("privilege.can-delete-releases") && (
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="!text-destructive"
                          onClick={() => deleteAlbum(album)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center justify-between">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      const newPage = Math.max(currentPage - 1, 1);
                      setCurrentPage(newPage);
                      handleChange({ current: newPage, pageSize }, {}, {});
                    }}
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {/* Show first page if we're not near the beginning */}
                {currentPage > 3 && (
                  <>
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => {
                          setCurrentPage(1);
                          handleChange({ current: 1, pageSize }, {}, {});
                        }}
                        className="cursor-pointer"
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                    {currentPage > 4 && (
                      <PaginationItem>
                        <span className="px-3 py-2">...</span>
                      </PaginationItem>
                    )}
                  </>
                )}

                {/* Show pages around current page */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }

                  if (page < 1 || page > totalPages) return null;

                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => {
                          setCurrentPage(page);
                          handleChange({ current: page, pageSize }, {}, {});
                        }}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                {/* Show last page if we're not near the end */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <PaginationItem>
                        <span className="px-3 py-2">...</span>
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => {
                          setCurrentPage(totalPages);
                          handleChange(
                            { current: totalPages, pageSize },
                            {},
                            {}
                          );
                        }}
                        className="cursor-pointer"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      const newPage = Math.min(currentPage + 1, totalPages);
                      setCurrentPage(newPage);
                      handleChange({ current: newPage, pageSize }, {}, {});
                    }}
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <div className="text-sm text-gray-500 mt-2">
              Showing {startIndex + 1}-
              {Math.min(startIndex + pageSize, totalCount)} of {totalCount}{" "}
              releases
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
